import type { Theme, WithStyles } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'
import Add from '@material-ui/icons/Add'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { getObjectLabels } from '@paintscout/util/builder'
import classnames from 'classnames'
import type { OptionsDocument, PresentationOption, QuoteDocument, QuoteOptions, QuoteViews } from 'paintscout'
import React from 'react'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import Tab from '../Tab'
import Tooltip from '../Tooltip'
import IconButton from '../IconButton'

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      display: 'flex',
      // display: 'grid'
      flexWrap: 'wrap',
      height: '100%',
      '& button': {
        ...theme.typography.overline
      }
    },
    tab: {
      // paddingTop: theme.spacing(3),
      // paddingBottom: theme.spacing(3),
      fontSize: `${theme.typography.pxToRem(20)} !important`,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      marginRight: theme.spacing(4),
      minHeight: theme.spacing(7),
      '&, & *': {
        fontSize: `${theme.typography.pxToRem(20)} !important`
      },
      '& .MuiTab-wrapper': {
        fontSize: `${theme.typography.pxToRem(20)} !important`,
        '&, & *': {
          fontSize: `${theme.typography.pxToRem(20)} !important`
        }
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: 'inherit',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: 0,
        paddingRight: 0,
        marginRight: theme.spacing(3),
        minHeight: 'fit-content',
        '&, & *': {
          fontSize: 'inherit'
        },
        '& .MuiTab-wrapper': {
          fontSize: 'inherit',
          '&, & *': {
            fontSize: 'inherit'
          }
        }
      },
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(2)
      }
    },
    containerWrapper: {
      display: 'flex',
      alignItems: 'center'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '3em',
      // marginRight: 30,
      // background: theme.paintscout.palette.primary,
      // padding: `0 15px`,
      paddingRight: theme.spacing(2),
      marginRight: 5,
      '& img': {
        maxHeight: '100%'
      }
    },
    item: {},
    itemLabel: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap'
    },
    selectedItem: {
      color: theme.palette.primary.main,
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      opacity: 1
    },
    dragHelper: {},
    addButton: {
      borderRadius: theme.borderRadius.md,
      color: '#fff',
      background: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.5),
      margin: theme.spacing(1, 2, 1, 0),
      '&:hover': {
        color: '#fff',
        backgroundColor: theme.palette.primary.main,
        boxShadow: theme.shadows[1]
      }
    },
    dragIcon: {
      fontSize: `${theme.typography.pxToRem(20)} !important`,
      marginRight: theme.spacing(1),
      cursor: 'grab',
      color: theme.paintscout.palette.brand.Neutral.P70,
      '& svg': {
        fontSize: `${theme.typography.pxToRem(20)} !important`,
        width: `${theme.typography.pxToRem(20)} !important`,
        height: `${theme.typography.pxToRem(20)} !important`
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: theme.typography.pxToRem(18),
        marginRight: theme.spacing(0.5),
        '& svg': {
          fontSize: theme.typography.pxToRem(18),
          width: theme.typography.pxToRem(18),
          height: theme.typography.pxToRem(18)
        }
      }
    }
  }
})

export interface NavItem {
  value: string
  label: string
}

export interface NavTabsProps extends WithStyles {
  presentation?: PresentationOption
  options?: OptionsDocument
  quote?: QuoteDocument
  view?: QuoteViews
  value: string
  logo?: string
  disabled?: boolean
  onNavClick?: (ev: React.MouseEvent, value: string) => void

  onReorder?: (items: NavItem[]) => void
  allowAdd?: boolean
  onAddClick?: (ev: React.MouseEvent) => void
}

interface SortableNavContainerProps {
  children?: React.ReactNode
  classes?: any
}

const SortableNavContainer: React.ComponentClass<SortableContainerProps & SortableNavContainerProps, any> =
  SortableContainer(function NavWrapper({ children, ...props }: SortableNavContainerProps & WithStyles) {
    const classes = useStyles(props)

    return <div className={classes.root}>{children}</div>
  })

interface SortableNavItemProps {
  item: NavItem
  selected: boolean
  onNavClick?: (ev: React.MouseEvent, value: string) => void
  classes?: any
  allowDrag?: boolean
}
const SortableNavItem: React.ComponentClass<SortableElementProps & SortableNavItemProps, any> = SortableElement(
  ({ item, selected, onNavClick, allowDrag, ...props }: SortableNavItemProps & WithStyles) => {
    const classes = useStyles(props)

    return (
      <div>
        <div
          onClick={(ev: React.MouseEvent) => {
            onNavClick(ev, item.value)
          }}
        >
          <Tab
            classes={{
              root: classnames({ [classes.item]: true, [classes.selectedItem]: selected, [classes.tab]: true })
            }}
            label={
              <div className={classes.itemLabel}>
                {allowDrag && (
                  <DragIndicatorIcon
                    onClick={(e) => e.stopPropagation()}
                    id="drag-indicator"
                    className={classes.dragIcon}
                  />
                )}

                {item.label ? item.label : '(No Title)'}
              </div>
            }
          />
        </div>
      </div>
    )
  }
)

function NavTabs({
  value,
  logo,
  onNavClick,
  onAddClick,
  disabled,
  allowAdd,
  onReorder,
  presentation,
  options,
  quote,
  view,
  ...props
}: NavTabsProps) {
  const classes = useStyles(props)

  const items = getPresentationTabs({ presentation, options, quote, view })

  const shouldCancelStart = (event: any) => {
    // If the user is clicking on the drag-indicator (or any child of the drag-indicator), don't cancel the start
    return !event?.target?.closest('#drag-indicator') || disabled
  }

  return (
    <div className={classes.containerWrapper}>
      {logo && (
        <div className={classes.logo}>
          <img src={logo} />
        </div>
      )}
      <SortableNavContainer
        axis={'xy'}
        pressDelay={150}
        helperClass={classes.dragHelper}
        classes={classes}
        onSortEnd={handleSortEnd}
        shouldCancelStart={shouldCancelStart}
      >
        {items.map((item: NavItem, index: number) => (
          <SortableNavItem
            key={item.value}
            item={item}
            classes={classes}
            index={index}
            selected={(value && item.value === value) || (!value && index === 0)}
            onNavClick={onNavClick}
            allowDrag={!!onReorder && !disabled}
          />
        ))}
      </SortableNavContainer>
      {allowAdd && (
        <Tooltip
          content={
            'These pages make up the table of contents for your client to see. Click the ' +
            ' button to add a new page and drag and drop the page titles to rearrange them.'
          }
        >
          <IconButton className={classes.addButton} size="small" onClick={onAddClick}>
            <Add />
          </IconButton>
        </Tooltip>
      )}
    </div>
  )

  function handleSortEnd({ oldIndex, newIndex }) {
    onReorder(arrayMove(items, oldIndex, newIndex))
  }
}

function getDefaultPresentationTabs(args: { quote: QuoteDocument; options: OptionsDocument; view: string }) {
  const { quote, options, view } = args
  const quoteOptions = quote.options ? quote.options : ({} as Partial<QuoteOptions>)
  const objectLabels = getObjectLabels({ options })

  let tabs: any[]
  if (view === 'work-order') {
    tabs = [
      { value: 'quote', label: objectLabels.workOrder.value },
      { value: 'pictures', label: 'Pictures' }
    ]

    if (!quote.images || !quoteOptions.showWorkOrderFiles) {
      tabs = tabs.filter((item) => item.value !== 'pictures')
    }
  } else if (quote.is_invoice) {
    tabs = [
      { value: 'quote', label: 'Invoice' },
      { value: 'terms', label: 'Terms' }
    ]
  } else {
    tabs = [
      { value: 'quote', label: objectLabels.quote.value },
      { value: 'pictures', label: 'Pictures' },
      { value: 'terms', label: 'Terms' }
    ]

    if (!quote.images || !quoteOptions.showWorkOrderFiles) {
      tabs = tabs.filter((item) => item.name !== 'pictures')
    }
  }

  return tabs
}

function getPresentationTabs(args: {
  presentation: PresentationOption
  quote: QuoteDocument
  options: OptionsDocument
  view: string
}) {
  const { presentation, quote, options, view } = args
  const { pages } = presentation

  if (pages && pages.length > 1) {
    return pages
      .filter((page) => page?.advanced?.useForNav && (page?.active ?? true))
      .map((page) => {
        return {
          value: page.key,
          label: page.title,
          type: 'page'
        }
      })
  } else if (pages && pages.length === 1) {
    return pages[0].sections
      .filter((section) => section?.advanced?.useForNav && (section?.active ?? true) && !!section.title)
      .map((section) => {
        return {
          value: section.key,
          label: section.title,
          type: 'section'
        }
      })
  } else {
    return getDefaultPresentationTabs({ quote, options, view })
  }
}

export default NavTabs
